import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Box, Grid, GridItem, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import LoadingSpinner from '../../../../../../../components/Spinner';
import { useInsuredEmails } from '../../../../../../../services/insured-service';
import { getInfiniteListData } from '../../../../../../../services/util';
import EmailHistoryRow from '../EmailHistoryRow';

const EmailHistoryBody = ({ insuredId, recordId, order }) => {
  const query = useInsuredEmails({ insuredId, recordId, order });
  const { isLoading: emailsLoading, isError: emailsFailedLoading } = query;

  const emailData = useMemo(() => getInfiniteListData(query.data), [query]);

  const dataLength = useMemo(() => {
    if (query.data?.pages?.length) {
      return query.data.pages[0].total;
    }

    return 0;
  }, [query]);

  if (emailsLoading && !emailData) {
    return (
      <Box minH="100px">
        <LoadingSpinner minHeight="100px" />
      </Box>
    );
  }

  if (emailsLoading) {
    return (
      <Box minH="100px">
        <LoadingSpinner minHeight="100px" />
      </Box>
    );
  }

  if (emailsFailedLoading) {
    return (
      <Box minH="100px">
        <Text
          color="error.primary.600"
          textAlign="center"
        >
          There was an error loading this insured&apos;s email history
        </Text>
        <Text
          color="greyScale.800"
          fontStyle="italic"
          textAlign="center"
        >
          {emailsFailedLoading}
        </Text>
      </Box>
    );
  }

  if (!emailData?.length) {
    return (
      <Box minH="100px">
        <Text
          color="greyScale.800"
          textAlign="center"
        >
          No Email History
        </Text>
      </Box>
    );
  }

  return (
    <Box
      height="80vh"
      id="scrollableEmailHistoryBody"
      overflowY="auto"
    >
      <Grid
        alignItems="center"
        color="greyScale.800"
        gap={6}
        mb={2}
        px={2}
        templateColumns="repeat(20, 1fr)"
        textAlign="left"
        textStyle="bodySm500"
      >
        <GridItem colSpan={2}>Date</GridItem>
        <GridItem colSpan={1}>From</GridItem>
        <GridItem colSpan={5}>To</GridItem>
        <GridItem colSpan={6}>Subject</GridItem>
        <GridItem colSpan={5}>Project</GridItem>
        <GridItem colSpan={1}>View</GridItem>
      </Grid>
      {emailData.length && (
        <InfiniteScroll
          dataLength={dataLength}
          endMessage={(
            <Text color="greyScale.800" textAlign="center" textStyle="bodyMd">
              {`Showing all (${dataLength})`}
            </Text>
          )}
          hasMore={query.hasNextPage}
          loader={(
            <Text color="greyScale.800" textAlign="center" textStyle="bodyMd">
              Loading...
            </Text>
          )}
          next={() => query.fetchNextPage()}
          scrollableTarget="scrollableEmailHistoryBody"
          scrollThreshold={0.7}
        >
          {emailData.map(email => (
            <EmailHistoryRow
              date={email.createdAt}
              email={email}
              emailId={email.id}
              jobName={email?.job?.jobName || 'none'}
              key={email.id}
              sender={email?.sender}
              senderType={email.senderType}
              subject={email.subject}
              to={email.to}
            />
          ))}
        </InfiniteScroll>
      )}
    </Box>
  );
};

EmailHistoryBody.propTypes = {
  insuredId: PropTypes.number.isRequired,
  recordId: PropTypes.number.isRequired,
  order: PropTypes.string.isRequired
};

export default EmailHistoryBody;
